import { GitHubPR, GitHubCommit, GitHubBranch } from './githubData'
export interface LinkedGitData {
  prs: GitHubPR[]
  commits: GitHubCommit[]
  branches: GitHubBranch[]
}

/**
 * Find GitHub PRs, commits, and branches that reference a given issue key.
 * Matches case-insensitively in PR titles/bodies/branches, commit messages, and branch names.
 */
export function findLinkedGitData(
  issueKey: string,
  prs: GitHubPR[],
  commits: GitHubCommit[],
  branches: GitHubBranch[],
): LinkedGitData {
  if (!issueKey)
    return {
      prs: [],
      commits: [],
      branches: [],
    }

  // Escape special regex chars in issue key, match case-insensitively
  const escaped = issueKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const pattern = new RegExp(`\\b${escaped}\\b`, 'i')
  const linkedPrs = prs.filter(
    (pr) =>
      pattern.test(pr.title || '') ||
      pattern.test(pr.body || '') ||
      pattern.test(pr.head?.ref || ''),
  )
  const linkedCommits = commits.filter((c) =>
    pattern.test(c.commit?.message || c.message || ''),
  )
  const linkedBranches = branches.filter((b) => pattern.test(b.name || ''))
  return {
    prs: linkedPrs,
    commits: linkedCommits,
    branches: linkedBranches,
  }
}

/**
 * Build a map of issue key → linked git data for a list of issue keys.
 * More efficient than calling findLinkedGitData per issue.
 */
export function buildGitLinkMap(
  issueKeys: string[],
  prs: GitHubPR[],
  commits: GitHubCommit[],
  branches: GitHubBranch[],
): Record<string, LinkedGitData> {
  const map: Record<string, LinkedGitData> = {}
  for (const key of issueKeys) {
    map[key] = {
      prs: [],
      commits: [],
      branches: [],
    }
  }

  // Single pass through PRs
  for (const pr of prs) {
    const searchText = `${pr.title || ''} ${pr.body || ''} ${pr.head?.ref || ''}`
    for (const key of issueKeys) {
      const pattern = new RegExp(
        `\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i',
      )
      if (pattern.test(searchText)) {
        map[key].prs.push(pr)
      }
    }
  }

  // Single pass through commits
  for (const c of commits) {
    const msg = c.commit?.message || c.message || ''
    for (const key of issueKeys) {
      const pattern = new RegExp(
        `\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i',
      )
      if (pattern.test(msg)) {
        map[key].commits.push(c)
      }
    }
  }

  // Single pass through branches
  for (const b of branches) {
    const name = b.name || ''
    for (const key of issueKeys) {
      const pattern = new RegExp(
        `\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        'i',
      )
      if (pattern.test(name)) {
        map[key].branches.push(b)
      }
    }
  }
  return map
}
